/**
 * "Carts at acme.com": the carts of coworkers at the same business domain.
 *
 * A buyer can open a colleague's cart, change quantities or drop lines (the
 * change is recorded under their own name), or copy the lines into their own
 * cart to quote or buy them. Nothing shows for public mail hosts, where an
 * address says nothing about who else is on the team.
 */
import { useCallback, useEffect, useState } from "react";
import { CartItem } from "../common/models/cart";
import { useDimensionsUnit } from "../buyerStateDefs";
import { formatDimensions } from "../components/InventorySearch/helpers";
import {
  ActivityTrail,
  activityLine,
  actorLabel,
} from "../components/ActivityTrail";
import {
  CART_CHANGED_EVENT,
  ServerCart,
  ServerCartSummary,
  listCompanyCarts,
  loadCoworkerCart,
  saveCoworkerCart,
} from "./serverCart";

const day = (iso: string) => (iso ? iso.slice(0, 10) : "");

export function CompanyCartsPanel({
  viewerEmail,
  myCartItems,
  onCopyIntoMyCart,
}: {
  viewerEmail: string | null | undefined;
  /** Re-lists when the viewer's own cart changes (their row's count moves). */
  myCartItems: CartItem[];
  onCopyIntoMyCart: (items: CartItem[]) => void;
}) {
  const dimensionUnit = useDimensionsUnit((state) => state.dimensionsUnit);
  const [domain, setDomain] = useState<string | null>(null);
  const [carts, setCarts] = useState<ServerCartSummary[] | null>(null);
  const [open, setOpen] = useState<ServerCart | null>(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const refresh = useCallback(async () => {
    try {
      const result = await listCompanyCarts();
      setDomain(result.domain);
      setCarts(result.carts);
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not list the carts.");
    }
  }, []);

  useEffect(() => {
    void refresh();
    // A save of the viewer's own cart lands a moment later; list again then.
    const onChange = () => setTimeout(() => void refresh(), 1800);
    window.addEventListener(CART_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(CART_CHANGED_EVENT, onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, myCartItems.length]);

  if (!domain) return null;
  const coworkers = (carts || []).filter((c) => !c.mine);

  const productName = (product: any) => {
    const mats = product?.material?.dbMaterials || [];
    if (mats.length === 0) return product?.name || "Unnamed product";
    return `${mats[0]} ${product?.shape || ""} ${formatDimensions(product, dimensionUnit)}`.trim();
  };

  const openCart = async (uid: string) => {
    setBusy(uid);
    setNotice("");
    try {
      setOpen(await loadCoworkerCart(uid));
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not open that cart.");
    } finally {
      setBusy("");
    }
  };

  const saveOpen = async (items: CartItem[]) => {
    if (!open) return;
    setBusy("save");
    try {
      const saved = await saveCoworkerCart(open.ownerUid, {
        cartItems: items,
        totalShippingOverride: open.totalShippingOverride,
      });
      setOpen(saved);
      setNotice(`Saved to ${actorLabel(saved.owner, viewerEmail)}'s cart under your name.`);
      void refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save that cart.");
    } finally {
      setBusy("");
    }
  };

  const setQuantity = (index: number, value: number) => {
    if (!open) return;
    const items = open.cartItems.map((item, i) =>
      i === index
        ? {
            ...item,
            quantityRequired: {
              value,
              unit: item.quantityRequired?.unit || "",
            },
          }
        : item,
    );
    setOpen({ ...open, cartItems: items });
  };

  return (
    <div className="mt-4 rounded-xl border border-gray-200 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-[#00062e]">
          Carts at {domain}
        </h3>
        <span className="text-xs text-gray-500">
          Everyone signed in with an @{domain} address shares these.
        </span>
      </div>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      {notice && <p className="mt-2 text-xs text-green-700">{notice}</p>}
      {carts === null && !error && (
        <p className="mt-2 text-xs text-gray-500">Looking up your coworkers' carts...</p>
      )}
      {carts !== null && coworkers.length === 0 && (
        <p className="mt-2 text-xs text-gray-500">
          No coworker has a cart on their account yet. Yours is saved here as
          you change it, so a colleague can pick it up.
        </p>
      )}
      {coworkers.length > 0 && (
        <ul className="mt-2 divide-y divide-gray-100">
          {coworkers.map((c) => (
            <li key={c.ownerUid} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
              <div className="min-w-0">
                <span className="font-medium text-[#00062e]">
                  {c.owner.name || c.owner.email}
                </span>{" "}
                <span className="text-gray-500">({c.owner.email})</span>
                <div className="text-xs text-gray-500">
                  {c.itemCount} {c.itemCount === 1 ? "item" : "items"} · updated{" "}
                  {day(c.updatedAt)}
                  {c.lastActivity ? ` · ${activityLine(c.lastActivity, viewerEmail)}` : ""}
                </div>
              </div>
              <button
                type="button"
                className="text-xs text-blue-700 hover:underline disabled:text-gray-400"
                disabled={busy === c.ownerUid}
                onClick={() =>
                  open?.ownerUid === c.ownerUid ? setOpen(null) : void openCart(c.ownerUid)
                }
              >
                {open?.ownerUid === c.ownerUid
                  ? "Close"
                  : busy === c.ownerUid
                    ? "Opening..."
                    : "Open"}
              </button>
            </li>
          ))}
        </ul>
      )}

      {open && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm font-medium text-[#00062e]">
              {actorLabel(open.owner, viewerEmail)}'s cart
            </p>
            <ActivityTrail activity={open.activity} viewerEmail={viewerEmail} />
          </div>
          {open.cartItems.length === 0 && (
            <p className="mt-2 text-xs text-gray-500">This cart is empty.</p>
          )}
          {open.cartItems.length > 0 && (
            <table className="mt-2 min-w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-1 pr-2 font-medium">Item</th>
                  <th className="py-1 pr-2 font-medium">Quantity</th>
                  <th className="py-1 font-medium" />
                </tr>
              </thead>
              <tbody>
                {open.cartItems.map((item, index) => (
                  <tr key={`${item.product?.id || index}`} className="border-t border-gray-200">
                    <td className="py-1 pr-2 text-gray-800">{productName(item.product)}</td>
                    <td className="py-1 pr-2">
                      <input
                        type="number"
                        min={0}
                        step="any"
                        className="w-20 rounded border border-gray-300 px-1 py-0.5"
                        value={item.quantityRequired?.value ?? ""}
                        onChange={(e) => setQuantity(index, Number(e.target.value) || 0)}
                      />{" "}
                      <span className="text-gray-500">{item.quantityRequired?.unit || ""}</span>
                    </td>
                    <td className="py-1 text-right">
                      <button
                        type="button"
                        className="text-red-600 hover:underline"
                        onClick={() =>
                          setOpen({
                            ...open,
                            cartItems: open.cartItems.filter((_, i) => i !== index),
                          })
                        }
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
            <button
              type="button"
              className="rounded-full border border-[#00062e] px-3 py-1 font-medium text-[#00062e] hover:bg-[#00062e] hover:text-white disabled:opacity-50"
              disabled={busy === "save"}
              onClick={() => void saveOpen(open.cartItems)}
            >
              {busy === "save" ? "Saving..." : "Save changes to their cart"}
            </button>
            {open.cartItems.length > 0 && (
              <button
                type="button"
                className="text-blue-700 hover:underline"
                onClick={() => {
                  onCopyIntoMyCart(open.cartItems);
                  setNotice("Copied into your cart. Lines you already had keep the coworker's quantity.");
                }}
              >
                Copy into my cart
              </button>
            )}
            <span className="text-gray-500">
              Changes are saved under your name; {actorLabel(open.owner, viewerEmail)} sees
              who changed what.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
